import { Region } from '@/enums/region';
import { objectKeys } from '@/utils/object-keys';

export interface CollectibleState {
    highlightMissing: boolean;
    showCollected: boolean;
    showUncollected: boolean;
}
interface CollectiblePetsState {
    searchNoMaxLevel: boolean;
    searchNoRare: boolean;
}
interface BrowserStateIdk {
    auctions: {
        commoditiesCurrentExpansion: boolean;
    };
    convertible: {
        includePurchases: boolean;
    };
    decor: {
        highlightMissing: boolean;
        showCollected: boolean;
        showUncollected: boolean;
    };
    everything: {
        showCollected: boolean;
        showTransfers: boolean;
    };
    explore: {
        achievementId: number;
        bonusIds: string;
        modifierTreeId: number;
        questId: number;
        transmogSetId: number;
    };
    heirlooms: {
        highlightMissing: boolean;
        showCollected: boolean;
        showUncollected: boolean;
    };
    home: {
        activeView: string;
        groupSort: Record<string, string>;
        groupSortReverse: Record<string, boolean>;
        showTotalGold: boolean;
    };
    illusions: {
        highlightMissing: boolean;
        showCollected: boolean;
        showUncollected: boolean;
    };
    items: {
        highlightBagSize: boolean;
        highlightEnchants: boolean;
        highlightGems: boolean;
        highlightHeirlooms: boolean;
        highlightItemLevel: boolean;
        highlightUpgrades: boolean;
        itemLevelComparison: '<' | '<=' | '=' | '>=' | '>';
        itemLevelSlot: number;
        itemLevelValue: number;
        minimumBagSize: number;
    };
    itemsSearch: {
        name: string;
        locations: string[];
        qualities: string[];
    };
    journal: {
        filtersExpanded: boolean;

        highlightMissing: boolean;
        showCollected: boolean;
        showUncollected: boolean;
        showLockouts: boolean;

        showCloth: boolean;
        showLeather: boolean;
        showMail: boolean;
        showPlate: boolean;

        showCloaks: boolean;
        showWeapons: boolean;

        showConvertible: boolean;
        showMounts: boolean;
        showPets: boolean;
        showRecipes: boolean;
        showTokens: boolean;
        showTrash: boolean;

        showDungeonNormal: boolean;
        showDungeonHeroic: boolean;
        showDungeonMythic: boolean;
        showDungeonTimewalking: boolean;

        showRaidLfr: boolean;
        showRaidNormal: boolean;
        showRaidHeroic: boolean;
        showRaidMythic: boolean;
        showRaidMythicOld: boolean;
        showRaidTimewalking: boolean;
        showRaid10: boolean;
        showRaid25: boolean;
    };
    lockouts: {
        grouped: boolean;
        onlyWithLockout: boolean;
        sortBy: number;
    };
    matrix: {
        minLevel: number;
        showCharacterAs: 'level' | 'name';
        showCovenant: boolean;
        showEmptyRows: boolean;
        xAxis: string[];
        yAxis: string[];
    };
    professions: {
        patronOrdersUnknown: boolean;
        recipesIncludeTrainer: boolean;
        recipesOnlyCollectors: boolean;
    };
    reputations: {
        sortOrder: Record<string, number[]>;
    };
    settings: {
        selectedGroup: string;
        selectedView: string;
    };
    tokens: {
        highlightMissing: boolean;
        showCollected: boolean;
        showUncollected: boolean;
    };
    transmogSets: {
        mythicOnly: boolean;
    };
    vendors: {
        filtersExpanded: boolean;

        highlightMissing: boolean;
        showCollected: boolean;
        showUncollected: boolean;
        showCollectedPrices: boolean;

        showCloth: boolean;
        showLeather: boolean;
        showMail: boolean;
        showPlate: boolean;

        showCloaks: boolean;
        showCosmetics: boolean;
        showEnsembles: boolean;
        showWeapons: boolean;

        showDecor: boolean;
        showDragonriding: boolean;
        showIllusions: boolean;
        showMounts: boolean;
        showPets: boolean;
        showRecipes: boolean;
        showToys: boolean;

        showPvp: boolean;
        showTier: boolean;

        showAwakened: boolean;

        showAlliance: boolean;
        showHorde: boolean;

        hiddenCurrencies: number[];
    };
    worldQuests: {
        region: Region;
    };

    'collectible-customizations': CollectibleState;
    'collectible-mounts': CollectibleState;
    'collectible-pets': CollectibleState & CollectiblePetsState;
    'collectible-toys': CollectibleState;
}

const initialState: BrowserStateIdk = {
    auctions: {
        commoditiesCurrentExpansion: true,
    },
    convertible: {
        includePurchases: true,
    },
    decor: {
        highlightMissing: true,
        showCollected: true,
        showUncollected: true,
    },
    everything: {
        showCollected: true,
        showTransfers: true,
    },
    explore: {
        achievementId: 0,
        bonusIds: '',
        modifierTreeId: 0,
        questId: 0,
        transmogSetId: 0,
    },
    heirlooms: {
        highlightMissing: true,
        showCollected: true,
        showUncollected: true,
    },
    home: {
        activeView: '',
        groupSort: {},
        groupSortReverse: {},
        showTotalGold: false,
    },
    illusions: {
        highlightMissing: true,
        showCollected: true,
        showUncollected: true,
    },
    items: {
        highlightBagSize: false,
        highlightEnchants: false,
        highlightGems: false,
        highlightHeirlooms: false,
        highlightItemLevel: false,
        highlightUpgrades: false,
        itemLevelComparison: '<',
        itemLevelSlot: 0,
        itemLevelValue: 0,
        minimumBagSize: 0,
    },
    itemsSearch: {
        name: '',
        locations: ['1', '2', '4', '5', '100'],
        qualities: ['0', '1', '2', '3', '4', '5', '6', '7'],
    },
    journal: {
        filtersExpanded: false,

        highlightMissing: true,
        showCollected: true,
        showUncollected: true,
        showLockouts: true,

        showCloth: true,
        showLeather: true,
        showMail: true,
        showPlate: true,

        showCloaks: true,
        showWeapons: true,

        showConvertible: true,
        showMounts: true,
        showPets: true,
        showRecipes: true,
        showTokens: true,
        showTrash: true,

        showDungeonNormal: true,
        showDungeonHeroic: true,
        showDungeonMythic: true,
        showDungeonTimewalking: true,

        showRaidLfr: true,
        showRaidNormal: true,
        showRaidHeroic: true,
        showRaidMythic: true,
        showRaidMythicOld: true,
        showRaidTimewalking: true,
        showRaid10: true,
        showRaid25: true,
    },
    lockouts: {
        grouped: false,
        onlyWithLockout: false,
        sortBy: 0,
    },
    matrix: {
        minLevel: 0,
        showCharacterAs: 'level',
        showCovenant: false,
        showEmptyRows: false,
        xAxis: [],
        yAxis: [],
    },
    professions: {
        patronOrdersUnknown: true,
        recipesIncludeTrainer: true,
        recipesOnlyCollectors: false,
    },
    reputations: {
        sortOrder: {},
    },
    settings: {
        selectedGroup: '',
        selectedView: '',
    },
    tokens: {
        highlightMissing: true,
        showCollected: true,
        showUncollected: true,
    },
    transmogSets: {
        mythicOnly: false,
    },
    vendors: {
        filtersExpanded: false,
        highlightMissing: true,
        showCollected: true,
        showUncollected: true,
        showCollectedPrices: false,

        showCloth: true,
        showLeather: true,
        showMail: true,
        showPlate: true,

        showCloaks: true,
        showCosmetics: true,
        showEnsembles: true,
        showWeapons: true,

        showDecor: true,
        showDragonriding: true,
        showIllusions: true,
        showMounts: true,
        showPets: true,
        showRecipes: true,
        showToys: true,

        showPvp: true,
        showTier: true,

        showAwakened: true,

        showAlliance: true,
        showHorde: true,

        hiddenCurrencies: [],
    },
    worldQuests: {
        region: Region.US,
    },

    'collectible-customizations': {
        highlightMissing: true,
        showCollected: true,
        showUncollected: true,
    },
    'collectible-mounts': {
        highlightMissing: true,
        showCollected: true,
        showUncollected: true,
    },
    'collectible-pets': {
        highlightMissing: true,
        searchNoMaxLevel: true,
        searchNoRare: false,
        showCollected: true,
        showUncollected: true,
    },
    'collectible-toys': {
        highlightMissing: true,
        showCollected: true,
        showUncollected: true,
    },
};

class BrowserState {
    private _firstSave = true;
    private _state = $state<BrowserStateIdk>(initialState);
    private _storageKey = 'browserState';

    constructor() {
        const userJson = JSON.parse(localStorage.getItem(this._storageKey) ?? '{}');
        for (const key of objectKeys(this._state)) {
            const subKeys = objectKeys(this._state[key]);

            Object.assign(this._state[key], userJson[key] || {});
            for (const objectKey of objectKeys(this._state[key])) {
                if (subKeys.indexOf(objectKey) === -1) {
                    delete this._state[key][objectKey];
                }
            }
        }
    }

    get current() {
        return this._state;
    }

    // triggered via an $effect in @/user-home/Main.svelte, seems silly but it works
    save(state: BrowserStateIdk) {
        if (this._firstSave) {
            this._firstSave = false;
        } else {
            // console.log('save', state);
            localStorage.setItem(this._storageKey, JSON.stringify(state));
        }
    }
}

export const browserState = new BrowserState();
